/**
 * Minimal History-API router.
 *
 * The prototype resolved `#/segment/param` into `{ route, param }`. This keeps
 * that two-level model — every page in the site is either `/segment` or
 * `/segment/slug` — but serves it on real paths instead of a hash.
 */
import { useCallback, useEffect, useSyncExternalStore, type AnchorHTMLAttributes } from 'react'

const listeners = new Set<() => void>()

function emit() {
  for (const fn of listeners) fn()
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  window.addEventListener('popstate', fn)
  return () => {
    listeners.delete(fn)
    window.removeEventListener('popstate', fn)
  }
}

function getPathname() {
  return window.location.pathname
}

export function navigate(to: string) {
  if (to === window.location.pathname + window.location.search) return
  window.history.pushState(null, '', to)
  emit()
}

export function usePathname(): string {
  return useSyncExternalStore(subscribe, getPathname, () => '/')
}

export interface Location {
  /** First path segment, or `'home'` at the root — matches the prototype's `route`. */
  route: string
  /** Second path segment, or `null` — matches the prototype's `param`. */
  param: string | null
  pathname: string
}

export function useLocation(): Location {
  const pathname = usePathname()
  const parts = pathname.split('/').filter(Boolean)
  return { route: parts[0] || 'home', param: parts[1] ?? null, pathname }
}

/** Run `fn` whenever the route or param changes — the port of the prototype's `_sync`. */
export function useRouteChange(fn: () => void) {
  const { route, param } = useLocation()
  const stable = useCallback(fn, [fn])
  useEffect(() => {
    stable()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, param])
}

type AProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }

/**
 * Internal link. Falls back to normal browser navigation for modified clicks,
 * new-tab clicks and any absolute or external href.
 */
export function A({ href, onClick, ...rest }: AProps) {
  const isInternal = href.startsWith('/') && !href.startsWith('//')
  return (
    <a
      href={href}
      onClick={(e) => {
        onClick?.(e)
        if (!isInternal) return
        if (e.defaultPrevented) return
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        if (rest.target && rest.target !== '_self') return
        e.preventDefault()
        navigate(href)
      }}
      {...rest}
    />
  )
}
